import tracking from 'dibs-tracking';
import serverVars from 'server-vars';
import { Context } from '@datadog/browser-core';
import { BROWSE_TYPES, PAGE_TYPE } from 'dibs-constants/exports/pageTypes';

/**
 * `responseStart` is a metric provided by the Resource Timing Level 2 API.
 *
 * Spec - https://www.w3.org/TR/resource-timing-2/#dom-performanceresourcetiming
 * Order of `Navigation Timing` metrics - https://w3c.github.io/navigation-timing/#processing-model
 */
const PERF_ENTRY_DEFAULT_START_MARK = 'responseStart';

const PERF_METRIC_BROWSER_FCP = 'first-contentful-paint';
const PERF_METRIC_BROWSER_FID = 'first-input';
const PERF_METRIC_CUSTOM_HYDRATE = 'react-hydrate';

/**
 * Configuration objects to determine what will be uploaded to Google Analytics
 * and Datadog.
 */
const UPLOAD_TO_DD = [PERF_METRIC_BROWSER_FID];
const UPLOAD_TO_GA = [
    PERF_METRIC_BROWSER_FCP,
    PERF_METRIC_BROWSER_FID,
    // commenting out hydrate metric temporarily to
    // accelerate collection of samples for FCP and FID
    // PERF_METRIC_CUSTOM_HYDRATE
];
/**
 * GA limits how many samples they will process daily, therefore, we're only
 * firing performance metrics for these page types to more quickly gather data
 * https://developers.google.com/analytics/devguides/collection/analyticsjs/user-timings#sampling_considerations
 */
const UPLOAD_TO_GA_PAGETYPES = [PAGE_TYPE.PDP, ...BROWSE_TYPES, PAGE_TYPE.SEARCH];

function shouldUploadToDataDog(name: string): boolean {
    return UPLOAD_TO_DD.includes(name);
}
function shouldUploadToAnalytics(name: string): boolean {
    const pageType = serverVars.get('settings.pageType');
    return (
        !serverVars.get('enablers.disableGaPerformanceTracking') &&
        UPLOAD_TO_GA.includes(name) &&
        UPLOAD_TO_GA_PAGETYPES.includes(pageType)
    );
}

/**
 * Use these constants when calling mark or measure.
 */
export const PERF_METRICS = {
    HYDRATE: PERF_METRIC_CUSTOM_HYDRATE,
};

const supportsPerformanceObserver =
    typeof window === 'object' &&
    window.PerformanceObserver &&
    !!window.PerformanceObserver.supportedEntryTypes;

// Know when the entry types we would like to use are not supported.
function isSupportedPerfEntryType(entryTypes: string[]): boolean {
    for (const entryType of entryTypes) {
        if (!window.PerformanceObserver.supportedEntryTypes.includes(entryType)) {
            return false;
        }
    }
    return true;
}

function msToNs(duration: number): number {
    // datadog stores duration in ns and PerformanceEntry gives use ms.
    // to stay consistent we will convert duration to ns.
    return +(duration * 1e6).toFixed(0);
}

function roundToMs(milliseconds: number): number {
    return Math.round(milliseconds);
}

function trackUserTiming(timingCategory: string, timingVar: string, timingValue: number): void {
    const pageType = serverVars.get('settings.pageType');
    let timingLabel = '';
    // mimicking convention used for GA pageview page types for consistency
    if (BROWSE_TYPES.includes(pageType) || pageType === PAGE_TYPE.SEARCH) {
        timingLabel = 'Results';
    } else if (pageType === PAGE_TYPE.PDP) {
        timingLabel = 'Products';
    }

    if (timingLabel) {
        tracking.trackUserTiming({
            category: timingCategory,
            var: timingVar,
            value: roundToMs(timingValue),
            label: timingLabel,
        });
    }
}
function addDatadogUserAction(name: string, context: Context): void {
    if (window.DD_RUM) {
        window.DD_RUM.addUserAction(name, context);
    }
}

export function markPerfMetric(name: string): void {
    if (supportsPerformanceObserver) {
        window.performance.mark(name);
    }
}

export function measurePerfMetric(
    name: string,
    /**
     * In most cases we will want to measure things from the default mark, so not to include server
     * response times.
     */
    startMark = PERF_ENTRY_DEFAULT_START_MARK,
    endMark?: string
): void {
    if (supportsPerformanceObserver) {
        window.performance.measure(name, startMark, endMark);
    }
}

export function initCustomPerformanceReporting(): void {
    if (!supportsPerformanceObserver) {
        return;
    }

    if (isSupportedPerfEntryType(['measure'])) {
        const customObserver = new PerformanceObserver(list => {
            for (const entry of list.getEntries()) {
                const name = entry.name;
                if (shouldUploadToDataDog(name)) {
                    addDatadogUserAction(name, {
                        duration: msToNs(entry.duration),
                    });
                }

                if (shouldUploadToAnalytics(name)) {
                    if (entry.duration !== 0) {
                        trackUserTiming(entry.name, 'duration', entry.duration);
                    }
                    if (entry.startTime !== 0) {
                        trackUserTiming(entry.name, 'startTime', entry.startTime);
                    }
                    // A `startTime` of 0 is valid for when we're calculating `endTime`
                    if (entry.duration !== 0 && entry.startTime >= 0) {
                        trackUserTiming(entry.name, 'endTime', entry.startTime + entry.duration);
                    }
                }
            }
        });

        customObserver.observe({
            entryTypes: ['measure'],
        });
    }

    /**
     * Register separate observers for buffered performance metrics, because the `buffered` flag
     * is only available with a single entry type.
     *
     * FID - First Input Delay.
     */
    if (isSupportedPerfEntryType([PERF_METRIC_BROWSER_FID])) {
        const fidObserver = new PerformanceObserver(list => {
            for (const entry of list.getEntries()) {
                // @ts-ignore processingStart on PerformanceEntryList is not in official spec yet.
                const processingStart = entry.processingStart;
                const duration = processingStart - entry.startTime;
                /**
                 * Use entry type instead of name, name contains the user interaction event name
                 * that was used to calculate the FID.
                 */
                if (shouldUploadToDataDog(entry.entryType)) {
                    addDatadogUserAction('First Input Delay', {
                        duration: msToNs(duration),
                    });
                }
                if (shouldUploadToAnalytics(entry.entryType)) {
                    trackUserTiming(entry.entryType, 'delay', duration);
                }

                // after getting 'first-input' entry, we can safely disconnect observer
                fidObserver.disconnect();
            }
        });

        fidObserver.observe({
            type: PERF_METRIC_BROWSER_FID,
            buffered: true,
        });
    }

    /**
     * FCP - First Contentful Paint
     */
    if (isSupportedPerfEntryType(['paint'])) {
        const fcpObserver = new PerformanceObserver(list => {
            for (const entry of list.getEntries()) {
                if (shouldUploadToAnalytics(entry.name)) {
                    trackUserTiming(entry.name, 'startTime', entry.startTime);
                }
                // after getting 'first-contentful-paint' entry, we can safely disconnect observer
                fcpObserver.disconnect();
            }
        });

        fcpObserver.observe({
            type: 'paint',
            buffered: true,
        });
    }
}
